import React, { FC } from "react";
import Select from "react-select";

import { FormikProps } from "formik";
import {
  Form as FormControl,
  Col,
  FloatingLabel,
  Row,
  Alert,
} from "react-bootstrap";
import InputGroup from "react-bootstrap/InputGroup";
import Stack from "react-bootstrap/Stack";
import { useTranslation } from "react-i18next";
import { InfoCircleFill } from "react-bootstrap-icons";
import { FormControlsValues } from "./OffenceStepTypes";

type FormLocationInputsSectionProps = {
  formikProps: FormikProps<FormControlsValues>;
  citiesOptions: { value: number; label: string }[];
  streetsOptions: { value: number; label: string }[];
  setChosenCity: React.Dispatch<{ value: number; label: string }>;
  setMapCenter: React.Dispatch<{ lat: number; lng: number }>;
  isMapCenterSetFromSelectedReport: boolean;
  setIsMapCenterSetFromSelectedReport: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

const LocationInputsFormSection: FC<FormLocationInputsSectionProps> = ({
  formikProps,
  citiesOptions,
  streetsOptions,
  setChosenCity,
  setMapCenter,
  isMapCenterSetFromSelectedReport,
  setIsMapCenterSetFromSelectedReport,
}) => {
  const { t } = useTranslation();
  const {
    values,
    setFieldValue,
    errors,
    isSubmitting,
    touched,
    initialValues,
  } = formikProps;

  const resetFields = () => {
    const isReportIsInterstate = initialValues.reportIsInterstate;
    setFieldValue(
      "reportKm",
      isReportIsInterstate ? initialValues.reportKm : ""
    );
    setFieldValue(
      "houseNumber",
      isReportIsInterstate ? initialValues.houseNumber : ""
    );
    setFieldValue(
      "reportRoad",
      isReportIsInterstate ? initialValues.reportRoad : ""
    );
    setFieldValue(
      "street",
      isReportIsInterstate
        ? { label: t("chooseStreet"), value: "" }
        : initialValues.street
    );
    setFieldValue(
      "city",
      isReportIsInterstate
        ? { label: t("chooseCity"), value: "" }
        : initialValues.city
    );
    setMapCenter({
      lat: parseFloat(initialValues?.latitude),
      lng: parseFloat(initialValues?.longitude),
    });
  };
  return (
    <div>
      <InputGroup hasValidation>
        <Stack className="mb-3">
          <Row className="violation-type-container default-vertical-padding">
            <FormControl.Group as={Col} md="6" className="m-3">
              <FormControl.Check // prettier-ignore
                type="radio"
                label={t("reportIsInterstate")}
                disabled={isSubmitting}
                inline
                checked={values?.reportIsInterstate}
                name="reportIsInterstate"
                onChange={() => {
                  setFieldValue(
                    "reportIsInterstate",
                    !values?.reportIsInterstate
                  );
                  resetFields();
                }}
              />
              <FormControl.Check // prettier-ignore
                type="radio"
                label={t("reportIsntInterstate")}
                disabled={isSubmitting}
                inline
                checked={!values?.reportIsInterstate}
                name="reportIsInterstate"
                onChange={() => {
                  setFieldValue(
                    "reportIsInterstate",
                    !values?.reportIsInterstate
                  );
                  resetFields();
                }}
              />
            </FormControl.Group>
          </Row>

          {!values?.reportIsInterstate ? (
            <Stack className="mb-3">
              <Row className="violation-type-container default-vertical-padding">
                <FormControl.Group as={Col} md="5" className="m-3">
                  <Select
                    options={citiesOptions}
                    placeholder={t("city")}
                    isRtl
                    name="city"
                    value={values?.city}
                    onChange={(v) => {
                      setChosenCity(v);
                      setFieldValue("city", v);
                      setFieldValue("street", {
                        label: t("chooseStreet"),
                        value: "",
                      }); // reset street if city was changed
                      setFieldValue("houseNumber", "");
                    }}
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        borderColor: errors?.city?.value ? "red" : "#d4d4d4",
                        backgroundColor: "transparent",
                        margin: "0.5rem 0",
                      }),
                    }}
                  />
                </FormControl.Group>

                {!!values?.city?.value && (
                  <FormControl.Group as={Col} md="5" className="m-3">
                    <Select
                      options={streetsOptions}
                      placeholder={t("street")}
                      isRtl
                      name="street"
                      value={values?.street}
                      onChange={(v) => {
                        setFieldValue("street", v);
                      }}
                      styles={{
                        control: (baseStyles) => ({
                          ...baseStyles,
                          borderColor: errors?.street?.value
                            ? "red"
                            : "#d4d4d4",

                          backgroundColor: "transparent",
                          margin: "0.5rem 0",
                        }),
                      }}
                    />
                  </FormControl.Group>
                )}

                {!!values?.city?.value && (
                  <FormControl.Group as={Col} md="5" className="m-3">
                    <FloatingLabel
                      controlId="floatingInput"
                      className="text-right"
                      label={t("houseNumber")}
                    >
                      <FormControl.Control
                        type="number"
                        size="lg"
                        name="houseNumber"
                        value={values?.houseNumber}
                        placeholder={t("houseNumber")}
                        className="square border border-secondary form-control report-input"
                        isInvalid={!!errors?.houseNumber}
                        onKeyDown={(e) =>
                          ["e", "E", "+", "-"].includes(e.key) &&
                          e.preventDefault()
                        }
                        onChange={(event) => {
                          const inputValue = event.target.value;
                          if (inputValue.length > 4) {
                            // Do not update the value if it exceeds the limit
                            return;
                          }
                          if (inputValue === "") {
                            // Handle erasing the value by setting it to an empty string
                            setFieldValue("houseNumber", "");
                          } else {
                            setFieldValue("houseNumber", inputValue);
                          }
                        }}
                        min={0}
                        maxLength={4}
                      />
                      {errors?.houseNumber && (
                        <FormControl.Control.Feedback type="invalid">
                          {errors.houseNumber}
                        </FormControl.Control.Feedback>
                      )}
                    </FloatingLabel>
                  </FormControl.Group>
                )}
              </Row>
              {(errors?.city?.value || errors?.street?.value) && (
                <Alert variant="danger">
                  <p>
                    {errors?.city?.value && (
                      <InfoCircleFill
                        size={15}
                        color="red"
                        className="alert-description-icon"
                        style={{ margin: "0 10px" }}
                      />
                    )}
                    {errors?.city?.value ? errors?.city?.value : ""}
                  </p>
                  {!!values?.city?.value && (
                    <p>
                      {errors?.street?.value && (
                        <InfoCircleFill
                          size={15}
                          color="red"
                          className="alert-description-icon"
                          style={{ margin: "0 10px" }}
                        />
                      )}
                      {errors?.street?.value ? errors?.street?.value : ""}
                    </p>
                  )}
                </Alert>
              )}
            </Stack>
          ) : (
            <Stack className="mb-3">
              <Row className="violation-type-container default-vertical-padding">
                <FormControl.Group as={Col} md="5" className="m-3">
                  <FloatingLabel
                    controlId="floatingInput"
                    className="text-right"
                    label={t("reportRoad")}
                  >
                    <FormControl.Control
                      type="text"
                      size="lg"
                      name="reportRoad"
                      value={values?.reportRoad}
                      onChange={(event) => {
                        setFieldValue("reportRoad", event.target.value);
                        setIsMapCenterSetFromSelectedReport(false);
                      }}
                      placeholder={t("reportRoad")}
                      className="square border border-secondary form-control report-input"
                      isInvalid={touched.reportRoad && !!errors?.reportRoad}
                      maxLength={20}
                    />
                    {errors?.reportRoad && (
                      <FormControl.Control.Feedback type="invalid">
                        {errors.reportRoad}
                      </FormControl.Control.Feedback>
                    )}
                    {/* {roadLocationError && roadLocationError} */}
                  </FloatingLabel>
                </FormControl.Group>
                <FormControl.Group as={Col} md="5" className="m-3">
                  <FloatingLabel
                    controlId="floatingInput"
                    className="text-right"
                    label={t("reportLocationKm")}
                  >
                    <FormControl.Control
                      type="text"
                      size="lg"
                      name="reportKm"
                      value={values?.reportKm}
                      placeholder={t("reportLocationKm")}
                      className="square border border-secondary form-control report-input"
                      isInvalid={touched.reportKm && !!errors?.reportKm}
                      onKeyDown={(e) =>
                        ["e", "E", "+", "-"].includes(e.key) &&
                        e.preventDefault()
                      }
                      onChange={(event) => {
                        const inputValue = event.target.value;
                        if (inputValue.length > 40) {
                          // Do not update the value if it exceeds the limit
                          return;
                        }
                        if (inputValue === "") {
                          // Handle erasing the value by setting it to an empty string
                          setFieldValue("reportKm", "");
                        } else {
                          setFieldValue("reportKm", inputValue);
                        }
                        setIsMapCenterSetFromSelectedReport(false);
                      }}
                      min={0}
                      maxLength={40}
                    />
                    {errors?.reportKm && (
                      <FormControl.Control.Feedback type="invalid">
                        {errors.reportKm}
                      </FormControl.Control.Feedback>
                    )}
                  </FloatingLabel>
                </FormControl.Group>
              </Row>
            </Stack>
          )}
        </Stack>
      </InputGroup>
    </div>
  );
};
export default LocationInputsFormSection;
